/**
 * Live monitor — who is in the customer app RIGHT NOW and what they're doing.
 *
 * Online is TIME-BASED: state !== 'offline' && now - lastSeen < window. Because
 * that depends on the wall clock, we re-classify on a ticker (useNow) as well as
 * on every Firestore snapshot, so nobody stays "online" forever.
 */
import { useEffect, useMemo } from 'react';
import type { Presence, User } from '@mitang/shared';
import { usePresence, useUsers } from '../hooks/useFirestore';
import { doc, setDoc } from 'firebase/firestore';
import { COLLECTIONS, APP_CONFIG_DOC } from '@mitang/shared';
import { db } from '../lib/firebase';
import { useNow } from '../hooks/useNow';
import { Card, SectionHeader, Badge } from '../components/ui';
import { EmptyState, ErrorState, LoadingState } from '../components/States';
import { formatIndianNumber } from '../lib/format';
import { isOnline, isRecentlyActive, activityLabel } from '../lib/presence';

/** "just now" under 5s, else "Xs ago / Xm ago". Ticks with useNow. */
function seenAgo(lastSeen: number, now: number): string {
  const diff = Math.max(0, now - lastSeen);
  const sec = Math.round(diff / 1000);
  if (sec < 5) return 'just now';
  if (sec < 60) return `${sec}s ago`;
  const min = Math.round(sec / 60);
  if (min < 60) return `${min}m ago`;
  const hr = Math.round(min / 60);
  return `${hr}h ago`;
}

function stateTone(state: Presence['state']): 'success' | 'warn' | 'neutral' {
  if (state === 'online') return 'success';
  if (state === 'background') return 'warn';
  return 'neutral';
}

/** Display name: the dealer's profile name, else their email, else the uid. */
function displayName(p: Presence, user?: User): string {
  return user?.name || p.name || user?.email || p.email || 'Unknown dealer';
}

/** Secondary line: company · city when known, otherwise the email. */
function displaySub(p: Presence, user?: User): string {
  const company = user?.companyName || p.companyName;
  const city = user?.city || p.city;
  const parts = [company, city].filter(Boolean).join(' · ');
  if (parts) return parts;
  const email = user?.email || p.email;
  // Don't repeat the email if it's already the headline.
  return email && displayName(p, user) !== email ? email : '';
}

function OnlineCard({ p, now, user }: { p: Presence; now: number; user?: User }): JSX.Element {
  const sub = displaySub(p, user);
  return (
    <Card className="p-4">
      <div className="flex items-start gap-3">
        <span aria-hidden className="relative mt-1.5 flex h-2.5 w-2.5 flex-shrink-0">
          <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-emerald-400 opacity-75" />
          <span className="relative inline-flex h-2.5 w-2.5 rounded-full bg-emerald-500" />
        </span>
        <div className="min-w-0 flex-1">
          <div className="flex items-center justify-between gap-2">
            <p className="truncate text-sm font-semibold text-slate-900">{displayName(p, user)}</p>
            {p.state === 'background' && <Badge tone="warn">backgrounded</Badge>}
          </div>
          {sub && <p className="truncate text-xs text-slate-500">{sub}</p>}
          <p className="mt-2 truncate text-sm font-medium text-emerald-700" title={activityLabel(p)}>
            {activityLabel(p)}
          </p>
          <p className="mt-0.5 text-[11px] text-slate-400">{seenAgo(p.lastSeen, now)}</p>
        </div>
      </div>
    </Card>
  );
}

/**
 * Tell dealer apps to report presence while this page is open (see
 * AppConfig.livePresenceUntil). Refreshed every 4 minutes; cleared on unmount.
 */
function useLiveWatchWindow(): void {
  useEffect(() => {
    const ref = doc(db, COLLECTIONS.appConfig, APP_CONFIG_DOC);
    const ping = (ms: number) =>
      setDoc(ref, { livePresenceUntil: Date.now() + ms }, { merge: true }).catch(() => undefined);
    void ping(5 * 60 * 1000);
    const t = setInterval(() => void ping(5 * 60 * 1000), 4 * 60 * 1000);
    return () => {
      clearInterval(t);
      void ping(0); // stop dealer heartbeats immediately
    };
  }, []);
}

export function LivePage(): JSX.Element {
  useLiveWatchWindow();
  const presence = usePresence();
  // Admins may read all users; this join guarantees a real name/email on every
  // row regardless of what the (client-written) presence doc contains.
  const users = useUsers();
  const byUid = useMemo(
    () => new Map((users.data ?? []).map((u) => [u.id, u])),
    [users.data],
  );
  const now = useNow(4000);

  const { online, recent } = useMemo(() => {
    const on: Presence[] = [];
    const rec: Presence[] = [];
    for (const p of presence.data) {
      if (isOnline(p, now)) on.push(p);
      else if (isRecentlyActive(p, now)) rec.push(p);
    }
    // Both arrays already come lastSeen-desc from the query.
    return { online: on, recent: rec };
  }, [presence.data, now]);

  const onlineCount = online.length;
  const totalTracked = presence.data.length;

  return (
    <div className="space-y-6">
      <SectionHeader
        title="Live monitor"
        subtitle="Dealers active in the app right now, and what they're looking at."
      />

      {presence.error ? (
        <ErrorState message={presence.error} />
      ) : presence.loading ? (
        <LoadingState label="Connecting to live presence…" />
      ) : (
        <>
          {/* Headline count */}
          <Card className="flex flex-wrap items-center justify-between gap-4 p-5">
            <div className="flex items-center gap-3">
              <span aria-hidden className="relative flex h-3 w-3">
                {onlineCount > 0 && (
                  <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-emerald-400 opacity-75" />
                )}
                <span
                  className={`relative inline-flex h-3 w-3 rounded-full ${
                    onlineCount > 0 ? 'bg-emerald-500' : 'bg-slate-300'
                  }`}
                />
              </span>
              <div>
                <p className="text-3xl font-semibold tracking-tight text-emerald-600">
                  {formatIndianNumber(onlineCount)}{' '}
                  <span className="text-lg font-medium text-slate-500">online now</span>
                </p>
                <p className="text-xs text-slate-500">
                  {formatIndianNumber(totalTracked)} dealers tracked
                </p>
              </div>
            </div>
            <Badge tone={onlineCount > 0 ? 'success' : 'neutral'}>Live · updates every few seconds</Badge>
          </Card>

          {/* Online now */}
          {onlineCount === 0 ? (
            <EmptyState
              title="No dealers online right now."
              hint="This list updates live as dealers open the app."
              icon="🟢"
            />
          ) : (
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 xl:grid-cols-3">
              {online.map((p) => (
                <OnlineCard key={p.uid} p={p} now={now} user={byUid.get(p.uid)} />
              ))}
            </div>
          )}

          {/* Recently active */}
          {recent.length > 0 && (
            <Card>
              <div className="border-b border-slate-100 px-5 py-4">
                <h2 className="text-sm font-semibold text-slate-900">Recently active</h2>
                <p className="text-xs text-slate-500">
                  Backgrounded or just left — seen in the last few minutes
                </p>
              </div>
              <ul className="divide-y divide-slate-100">
                {recent.map((p) => {
                  const user = byUid.get(p.uid);
                  const sub = displaySub(p, user);
                  return (
                    <li key={p.uid} className="flex items-center gap-3 px-5 py-3 opacity-70">
                      <span
                        aria-hidden
                        className={`h-2 w-2 flex-shrink-0 rounded-full ${
                          p.state === 'background' ? 'bg-amber-400' : 'bg-slate-300'
                        }`}
                      />
                      <div className="min-w-0 flex-1">
                        <p className="truncate text-sm text-slate-700">
                          {displayName(p, user)}
                          {sub && <span className="text-slate-400"> · {sub}</span>}
                        </p>
                        <p className="truncate text-[11px] text-slate-400">{activityLabel(p)}</p>
                      </div>
                      <div className="flex flex-shrink-0 items-center gap-2">
                        <Badge tone={stateTone(p.state)}>{p.state}</Badge>
                        <span className="text-[11px] text-slate-400">{seenAgo(p.lastSeen, now)}</span>
                      </div>
                    </li>
                  );
                })}
              </ul>
            </Card>
          )}
        </>
      )}
    </div>
  );
}
